import React, {Component} from 'react';
import {connect} from 'react-redux';

import {defaultBaseRequestParameters} from '~/shared/src/utilities/modules/common';
import metaModel from '~/shared/src/utilities/metaModel';
import Reference from '~/shared/src/ci/Reference';

import ReferencePropertyTabPanel, {ComponentProps as ReferencePropertyTabPanelComponentProps} from '~/frontend/src/components/base/ItemFinder/detail/ReferencePanel/ReferenceDetailPanel/ReferencePropertyTabPanel';

import {StoreState} from '~/frontend/src/store';
import {getSubPanelProps} from '~/frontend/src/components/base/page/PageTabPanel/utilities';

import * as PanelActions from '~/frontend/src/actions/panel/common';
import {UnsetParamAction} from '~/frontend/src/actions/panel/common/unsetParam';
import {bindActionCreators, Dispatch} from 'redux';


const Actions = Object.assign( {}, PanelActions ) as any;
type UnsetParam = ( panelKey : string, paramKey : keyof ComponentProps ) => UnsetParamAction;


const defaultProps = {
    ...defaultBaseRequestParameters,

    /**
     * Show empty (not set) properties.
     */
    showEmptyProperties: true as boolean,

    /**
     * Show default values for properties.
     */
    showPropertyDefaults: true as boolean,

    /**
     * Show the MetaModel information as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode: false as boolean,

    /**
     * Show the MetaModel information as defined additionally for processed information.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames: false as boolean,

    /**
     * Is data in this form editable at all?
     */
    editable: false as boolean
};

type DefaultProps = typeof defaultProps;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The dataContext to access global.data with.
     */
    dataContext : string;

    /**
     * The reference.
     */
    reference : Reference;

    /**
     * Parameters for deep linking of a property
     */
    deepLinkProps? : ReferencePropertyTabPanelComponentProps[ 'deepLinkProps' ];

    /**
     * The props for sub panels
     */
    subPanelProps? : any;

    style? : React.CSSProperties;
};

type ActionProps = {

    // common panel actions
    unsetParam : UnsetParam;
};

export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & ActionProps;

/**
 * ReferenceDetailPanel
 */
class ReferenceDetailPanel extends Component<AllProps> {

    static defaultProps = defaultProps;

    /**
     * REDUX stuff
     */
    constructor( props : AllProps ) {
        super( props );
    }

    /**
     * REACT lifecycle method
     */
    componentDidMount() {
        this.clearDeepLink();
    }


    /* private ****************************************************************************************************************************/

    /**
     * Clears deepLinkProps.
     *
     * @private
     */
    clearDeepLink() {
        if ( this.props.deepLinkProps ) {
            this.props.unsetParam( this.props.uid, 'deepLinkProps' );
        }
    }

    isReferenceEditable() : boolean {
        const {
                  schema,
                  timestamp,
                  reference
              }                 = this.props,
              editableStatusIds = metaModel[ schema ].getEditableStatus().map( status => status.id );

        let retVal = false;

        if ( reference && timestamp === 'now' && editableStatusIds.includes( reference.statusId ) ) {
            retVal = true;
        }

        return retVal;
    }

    /**
     * REDUX stuff
     */
    render() {
        const {
                  uid,
                  dataContext,
                  schema,
                  status,
                  timestamp,
                  timezone,
                  reference,
                  rawMode,
                  showAdditionalRawNames,
                  showEmptyProperties,
                  showPropertyDefaults,
                  editable,
                  deepLinkProps,
                  subPanelProps,
                  style
              }           = this.props,
              tabPanelKey = 'propertiesTabPanel';

        let tabPanelProps = subPanelProps?.[ tabPanelKey ] ?? {};

        // apply deepLinkProps if
        if ( deepLinkProps ) {
            tabPanelProps = {
                ...tabPanelProps,
                deepLinkProps
            };
        }

        return <div className="yn-flex-layout-parent" style={style}>
            <ReferencePropertyTabPanel
                {...tabPanelProps}

                uid={`${uid}_${tabPanelKey}`}
                dataContext={dataContext}


                schema={schema}
                status={status}
                timestamp={timestamp}
                timezone={timezone}

                reference={reference}

                rawMode={rawMode}
                showAdditionalRawNames={showAdditionalRawNames}
                showPropertyDefaults={showPropertyDefaults}
                showEmptyProperties={showEmptyProperties}
                editable={editable && this.isReferenceEditable()}
            />
        </div>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState, ownProps : ComponentProps ) : Partial<ComponentProps> {
    let retVal : Partial<ComponentProps> = {};

    // this component is added via rowExpander functionality of DataTable,
    // that's why state update ends at the DataTable and never reaches this component
    // solution: watch for state updates

    // get subPanelProps directly from panelManager
    if ( ownProps.uid ) {
        retVal = {
            ...getSubPanelProps( ownProps.uid )
        };
    }

    // get reference direct from global data
    if ( global.data[ ownProps.dataContext ]?.references[ ownProps.reference?.id ] ) {
        retVal.reference = global.data[ ownProps.dataContext ].references[ ownProps.reference.id ];
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<Partial<ComponentProps>, undefined, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( ReferenceDetailPanel );
